import type { SoftphoneConnectionState } from "@dialstack/sdk-react/softphone";
import { useState } from "react";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";

import useVoiceDnd, { DND_DESCRIPTION } from "hooks/use-voice-dnd.hook";
import { VoiceCalendarBusy } from "services/types/users.type";

import {
  type SoftphoneStatus,
  statusButtonSx,
  statusDotSx,
  statusMenuDotSx,
  statusMenuHeaderSx,
  statusMenuItemSx,
  statusMenuNoteSx,
  statusMenuPaperSx,
  statusMenuSx
} from "./dialstack-softphone-widget.styles";
import useSoftphoneBusySignals from "./use-softphone-busy-signals.hook";

const STATUS_LABELS: Record<SoftphoneStatus, string> = {
  available: "Available",
  "on-call": "On a call",
  dnd: "Do not disturb",
  recording: "Recording a scribe",
  connecting: "Connecting"
};

// A busy calendar shows as Do Not Disturb, named for what the user is doing.
const CALENDAR_BUSY_LABELS: Record<Exclude<VoiceCalendarBusy, "">, string> = {
  patient: "With a patient",
  meeting: "In a meeting"
};

const CALENDAR_RINGS_DESCRIPTION =
  "Set from your Suno calendar. Calls still ring here.";
const CALENDAR_SILENCED_DESCRIPTION =
  "Set from your Suno calendar. Incoming calls skip your devices until it ends.";
// The phone declines inbound calls itself while a scribe records (ENG-2001).
const RECORDING_DESCRIPTION =
  "Incoming calls are declined until the recording ends.";

/**
 * Do Not Disturb is the user's own choice, so it outranks whatever the line is
 * doing; a ringing or live call outranks "available"; and a phone that is still
 * (re)connecting is neither reachable nor busy. The widget unmounts on
 * `error`/`disconnected`, so no offline state is needed. Below a live call sit
 * the things Suno knows (ENG-2022): a busy calendar that is not silencing the
 * phone, then an ambient scribe recording. `dndEnabled` is the phone's real
 * DND, whoever turned it on.
 */
export function deriveSoftphoneStatus(
  connection: SoftphoneConnectionState,
  hasCallActivity: boolean,
  dndEnabled: boolean,
  isCalendarBusy = false,
  isRecording = false
): SoftphoneStatus {
  if (dndEnabled) return "dnd";
  if (connection !== "connected") return "connecting";
  if (hasCallActivity) return "on-call";
  if (isCalendarBusy) return "dnd";
  if (isRecording) return "recording";
  return "available";
}

type Props = {
  connection: SoftphoneConnectionState;
  hasCallActivity: boolean;
};

/**
 * The header status dot, as a control (ENG-1999): shows the user's presence
 * and opens a two-item menu — Available / Do not disturb — that writes
 * `voice_dnd_enabled` through the same hook as Settings → My Phone, so the
 * two stay in sync. Presence never goes through the browser SDK (its
 * `setPresence` is unimplemented); Suno's backend pushes DND to DialStack.
 */
export default function SoftphoneStatusButton({
  connection,
  hasCallActivity
}: Props) {
  // The hook flips the store optimistically, so the control never needs a
  // disabled/saving state (and a disabled button would silence its Tooltip).
  const { enabled: dndEnabled, setEnabled: setDnd } = useVoiceDnd();
  const { calendarBusy, calendarSilencesCalls, isRecording } =
    useSoftphoneBusySignals();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const status = deriveSoftphoneStatus(
    connection,
    hasCallActivity,
    dndEnabled || calendarSilencesCalls,
    Boolean(calendarBusy),
    isRecording
  );
  const calendarLabel =
    status === "dnd" && calendarBusy
      ? CALENDAR_BUSY_LABELS[calendarBusy]
      : undefined;
  const isCalendarStatus = Boolean(calendarLabel);
  const label = calendarLabel ?? STATUS_LABELS[status];
  // The user's own DND says the same thing the calendar would, so it wins.
  let description = "";
  if (dndEnabled) description = DND_DESCRIPTION;
  else if (calendarSilencesCalls) description = CALENDAR_SILENCED_DESCRIPTION;
  // A recording declines calls whatever the dot says, so never promise a ring.
  else if (isRecording) description = RECORDING_DESCRIPTION;
  else if (isCalendarStatus) description = CALENDAR_RINGS_DESCRIPTION;
  const note = dndEnabled ? "" : description;
  // Something other than the user is holding calls back, so "as usual" would
  // contradict the note right above it.
  const availableDescription =
    calendarSilencesCalls || isRecording
      ? "Calls ring here again afterwards."
      : "Calls ring here as usual.";
  const isOpen = Boolean(anchorEl);

  const close = () => setAnchorEl(null);
  const choose = (next: boolean) => {
    close();
    if (next !== dndEnabled) setDnd(next);
  };

  return (
    <>
      <Tooltip
        placement="top"
        title={description ? `${label} — ${description}` : label}
      >
        <IconButton
          aria-label={`Status: ${label}`}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          aria-controls={isOpen ? "suno-phone-status-menu" : undefined}
          className="suno-phone-no-drag"
          size="small"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={statusButtonSx}
        >
          <Box sx={statusDotSx(status)} />
        </IconButton>
      </Tooltip>
      <Menu
        id="suno-phone-status-menu"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={statusMenuSx}
        slotProps={{ paper: { sx: statusMenuPaperSx } }}
        MenuListProps={{
          "aria-label": "Set your status",
          // Focus lands on the first option, past the note, so a screen reader
          // would otherwise never hear why the status is what it is.
          "aria-describedby": note ? "suno-phone-status-note" : undefined
        }}
      >
        {/* "On a call" and "Connecting" are never options, so this is the
            only place a touch user can read them. */}
        <ListSubheader
          disableSticky
          role="presentation" // a `listitem` is not valid inside a `menu`
          sx={statusMenuHeaderSx}
        >
          Status: {label}
        </ListSubheader>
        {note && (
          <ListSubheader
            disableSticky
            role="presentation"
            id="suno-phone-status-note"
            data-testid="suno-phone-status-note"
            sx={statusMenuNoteSx}
          >
            {note}
          </ListSubheader>
        )}
        <MenuItem
          role="menuitemradio"
          aria-checked={!dndEnabled}
          selected={!dndEnabled}
          onClick={() => choose(false)}
          sx={statusMenuItemSx("available")}
        >
          <Box aria-hidden sx={statusMenuDotSx("available")} />
          <ListItemText
            primary="Available"
            secondary={availableDescription}
            primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
            secondaryTypographyProps={{ fontSize: 12 }}
          />
        </MenuItem>
        <MenuItem
          role="menuitemradio"
          aria-checked={dndEnabled}
          selected={dndEnabled}
          onClick={() => choose(true)}
          sx={statusMenuItemSx("dnd")}
        >
          <Box aria-hidden sx={statusMenuDotSx("dnd")} />
          <ListItemText
            primary="Do not disturb"
            secondary={DND_DESCRIPTION}
            primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
            secondaryTypographyProps={{ fontSize: 12 }}
          />
        </MenuItem>
      </Menu>
    </>
  );
}
